import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast, Button, Spinner, Badge, Modal, Input, Icon } from '../../components/ds';
import './MembersPage.css';

// Only show admin role badge; op/member don't need a visible label (AS-16)

export default function MembersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // member object
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/ads', { replace: true });
    }
  }, [user, navigate]);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await api.getMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      addToast('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchMembers();
    }
  }, [user, fetchMembers]);

  const handleToggleStatus = async (member) => {
    if (member.status === 'active') {
      setConfirmDeactivate(member);
    } else {
      try {
        await api.updateMemberStatus(member.id, 'active');
        addToast('已啟用成員', 'success');
        fetchMembers();
      } catch (err) {
        addToast('操作失敗：' + err.message);
      }
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmDeactivate) return;
    setDeactivating(true);
    try {
      await api.updateMemberStatus(confirmDeactivate.id, 'paused');
      addToast('已停用成員', 'success');
      setConfirmDeactivate(null);
      fetchMembers();
    } catch (err) {
      addToast('停用失敗：' + err.message);
      setConfirmDeactivate(null);
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="members-header">
        <h1 className="members-title h1 h1--lg">成員管理</h1>
        <Button onClick={() => setShowInvite(true)}>
          <Icon name="plus" size={14} />
          邀請成員
        </Button>
      </div>

      <div className="members-table-wrap">
        {members.length === 0 ? (
          <div className="members-empty">尚無成員資料</div>
        ) : (
          <table className="members-table">
            <thead>
              <tr>
                <th>名稱</th>
                <th>Email</th>
                <th>角色</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = user && user.userID === m.id;
                return (
                  <tr key={m.id}>
                    <td className="member-name">{m.name}</td>
                    <td className="member-email">{m.email}</td>
                    <td>
                      {m.role === 'admin'
                        ? <Badge variant="primary">管理員</Badge>
                        : <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>—</span>
                      }
                    </td>
                    <td>
                      {m.status === 'active'
                        ? <Badge variant="success">啟用</Badge>
                        : <Badge variant="danger">停用</Badge>
                      }
                    </td>
                    <td>
                      {m.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={isSelf}
                          title={isSelf ? '無法停用自己的帳號' : undefined}
                          onClick={() => handleToggleStatus(m)}
                          style={!isSelf ? { color: 'var(--badge-danger-text)', borderColor: 'var(--badge-danger-text)' } : undefined}
                        >
                          停用
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleToggleStatus(m)}
                          style={{ color: 'var(--badge-success-text)', borderColor: 'var(--badge-success-text)' }}
                        >
                          啟用
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {confirmDeactivate && (
        <Modal
          title="確認停用"
          onClose={() => setConfirmDeactivate(null)}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setConfirmDeactivate(null)} disabled={deactivating}>取消</Button>
              <Button onClick={handleConfirmDeactivate} disabled={deactivating}>
                {deactivating ? '停用中...' : '確認停用'}
              </Button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: 14 }}>
            確定要停用 <strong>{confirmDeactivate.name}</strong>？停用後該成員將無法登入。
          </p>
        </Modal>
      )}

      {showInvite && (
        <InviteMemberModal
          onClose={() => setShowInvite(false)}
          onSaved={() => {
            setShowInvite(false);
            fetchMembers();
          }}
        />
      )}
    </div>
  );
}

function InviteMemberModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      addToast('請填寫所有欄位', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.createMember({ name, email, password });
      addToast('成員已邀請', 'success');
      onSaved();
    } catch (err) {
      addToast('邀請失敗：' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="邀請成員"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>取消</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? '邀請中...' : '邀請'}
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <Input
          label="名稱"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="成員姓名"
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="member@example.com"
        />
      </div>
      <div style={{ marginBottom: 4 }}>
        <Input
          label="初始密碼"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="設定初始密碼"
        />
      </div>
    </Modal>
  );
}
